import CustomLayout from '@/components/CustomLayout'
import { Box, Text } from '@conexasaude/conexa-hero'
import { FunctionComponent, useCallback, useEffect } from 'react'
import { useStyles } from './styles'
import { useLocation, useParams } from 'react-router-dom'
import { DESCRIPTION, TITLE_SCREEN } from './constants'
import { RenderIf } from '@/components/RenderIf'
import { useConfirmAppointment } from '../../queries'
import AppointmentConfirmationStatus from '../../components/AppointmentConfirmationStatus'
import CustomTimezone from '@/components/CustomTimezone'
import { AppointmentConfirmationParams } from '../../types/en-us'
import { useConfirmAppointmentStore } from '../../store/ConfirmAppointment.store'
import { capitalize } from '@/utils/fnUtils'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { AnalyticEvent } from '@/modules/Signup/types/en-us'
import { SEND_TO } from '@/modules/Signup/constants'
import { useNavigation } from '@/hooks/useNavigation'

const ConfirmAppointment: FunctionComponent = () => {
  const styles = useStyles()
  const { pathname } = useLocation()
  const currentRoute = pathname?.split('/')?.[1]
  const { userToken, attendanceToken } =
    useParams<AppointmentConfirmationParams>()
  const {
    appointmentConfirmation,
    setAppointmentConfirmation,
    resetAppointmentConfirmation,
  } = useConfirmAppointmentStore()
  const { logAnalyticsEvent } = useFirebaseContext()
  const navigate = useNavigation()

  const { isLoading, isError, isSuccess, data, refetch } =
    useConfirmAppointment({
      userToken,
      attendanceToken,
    })

  const settingUpAnalyticsEvent = useCallback(
    (eventDefinitions: AnalyticEvent) => {
      logAnalyticsEvent(eventDefinitions?.eventName, {
        interaction: `${eventDefinitions?.interaction}`,
        options: {
          sendTo: SEND_TO,
        },
      })
    },
    [logAnalyticsEvent],
  )

  const redirectPatientForActivation = useCallback(() => {
    window.location.href = appointmentConfirmation?.url
  }, [appointmentConfirmation?.url])

  useEffect(() => {
    if (data) setAppointmentConfirmation(data)
  }, [data, redirectPatientForActivation, setAppointmentConfirmation])

  const handleAccessAccount = () => {
    settingUpAnalyticsEvent({
      eventName: 'at-patient-appointment-confirm-email',
      interaction: 'access_account_click',
    })
    if (data?.pendingActivation) {
      redirectPatientForActivation()
    } else {
      if (appointmentConfirmation?.url) {
        window.location.href = appointmentConfirmation?.url
      } else {
        navigate('/login')
      }

      resetAppointmentConfirmation()
    }
  }

  const handleTryAgain = () => {
    settingUpAnalyticsEvent({
      eventName: 'at-patient-appointment-confirm-email',
      interaction: 'try_again_click',
    })
    refetch()
  }

  const successDescription = data?.appointmentAlreadyConfirmed
    ? 'Acesse sua conta abaixo e confira os detalhes'
    : `A consulta com <span>${
        capitalize(
          appointmentConfirmation?.professionalName?.toLocaleLowerCase(),
        ) || ''
      }</span> no dia <span>${
        appointmentConfirmation?.dateScheduling || ''
      }</span>, está
    confirmada.`

  const successTitle = data?.appointmentAlreadyConfirmed
    ? 'Sua consulta já foi confirmada'
    : 'Sua consulta foi confirmada'

  return (
    <CustomLayout hideFooter>
      <div css={styles.containerWrapper}>
        <Box width="1039px" height="448px" spacing="small">
          <RenderIf condition={(!isError && !isSuccess) || isLoading}>
            <div css={styles.containerBox}>
              <div css={styles.loading} />
              <Text renderAs="h1" variant="headingLarge" textAlign="center">
                {TITLE_SCREEN[currentRoute]}
              </Text>
              <Text renderAs="p" variant="paragraphLarge" textAlign="center">
                {DESCRIPTION[currentRoute]}
              </Text>
            </div>
          </RenderIf>
          <RenderIf condition={isSuccess || data?.appointmentAlreadyConfirmed}>
            <AppointmentConfirmationStatus
              onClick={handleAccessAccount}
              title={successTitle}
              ilustration="user-phonesignal-ok"
              buttonText={
                data?.pendingActivation ? 'Ativar conta' : 'Acessar conta'
              }
              description={successDescription}
            />
          </RenderIf>
          <RenderIf
            condition={
              isError &&
              !data?.appointmentAlreadyConfirmed &&
              !appointmentConfirmation?.pendingActivation
            }
          >
            <AppointmentConfirmationStatus
              onClick={handleTryAgain}
              title="Não foi possível confirmar a consulta"
              ilustration="user-phonesignal-error"
              buttonText="Tentar novamente"
              description="Houve um erro ao tentar confirmar sua consulta. Por favor, tente novamente."
            />
          </RenderIf>
        </Box>
      </div>
      <div css={styles.footer}>
        <CustomTimezone
          showTimeZone={false}
          showfooterCopyrightText={false}
          tagEventName="at-patient-appointment-confirm-email"
          tagInteraction="help_click"
        />
      </div>
    </CustomLayout>
  )
}

export default ConfirmAppointment
